import localStorage from 'local-storage';
import { MENU_ORDER_TYPE_MAPPINGS, ORDER_PAYMENT_TYPE } from 'helpers/enums';

/**
 * Single emitter for the *store's own* analytics channels: Google Tag Manager,
 * GA4 (gtag.js) and the Meta Pixel.
 *
 * Each channel's snippet is only injected by head-template.xml when the store
 * has configured that ID, and the template exposes the configured IDs on
 * `window` (`__gtmId` / `__ga4Id` / `__pixelId`), so every send here is gated
 * on the channel actually being set up.
 *
 * This is deliberately separate from Firebase Analytics (`firebaseAnalyticsNO`
 * in src/firebase.js), which reports to Next Order's own shared GA4 property
 * rather than the store's. Call sites fire both: Firebase for the internal
 * cross-store funnel, this module so the store's own property has the same
 * picture.
 *
 * Consent is handled by the tag libraries themselves — Google Consent Mode v2
 * defaults and `fbq('consent', ...)`, both set in head-template.xml and updated
 * by helpers/consent.js — so nothing here needs its own consent gate.
 *
 * See ANALYTICS.md for the full event reference and the GA4/GTM setup each
 * store needs.
 */

// Keeps `purchase` from being sent twice for the same order. Four different
// payment paths reach a purchase, and Home's cleanup effect re-runs whenever a
// placed order is still in state (including after a reload), so dedupe on the
// order id rather than trusting any single call site to fire exactly once.
const TRACKED_PURCHASES_KEY = 'no_tracked_purchases';
const TRACKED_PURCHASES_KEPT = 20;

const PAYMENT_TYPE_LABELS = {
  [ORDER_PAYMENT_TYPE.unpaid]: 'unpaid',
  [ORDER_PAYMENT_TYPE.cash]: 'cash',
  [ORDER_PAYMENT_TYPE.card_online]: 'card_online',
  [ORDER_PAYMENT_TYPE.card_in_store]: 'card_in_store',
  [ORDER_PAYMENT_TYPE.mobile_terminal]: 'mobile_terminal',
  [ORDER_PAYMENT_TYPE.custom]: 'custom',
};

export const gtmEnabled = () =>
  typeof window !== 'undefined' && !!window.__gtmId;

export const ga4Enabled = () =>
  typeof window !== 'undefined' &&
  !!window.__ga4Id &&
  typeof window.gtag === 'function';

export const pixelEnabled = () =>
  typeof window !== 'undefined' &&
  !!window.__pixelId &&
  typeof window.fbq === 'function';

// Set `window.__analyticsDebug = true` in the console to see every send.
function debug(channel, name, params) {
  if (typeof window !== 'undefined' && window.__analyticsDebug) {
    // eslint-disable-next-line no-console
    console.log(`[analytics] ${channel} → ${name}`, params ?? {});
  }
}

// Analytics must never be able to break a checkout, so every send is wrapped.
function safely(channel, name, send) {
  try {
    send();
  } catch (error) {
    debug('error', `${channel} ${name}`, error);
  }
}

export function gtmPush(event, params = {}) {
  if (!gtmEnabled()) return;
  safely('gtm', event, () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
    debug('gtm', event, params);
  });
}

/**
 * GTM's built-in GA4 ecommerce mapping reads from a nested `ecommerce` object,
 * not from flat params, and it needs the previous push cleared first or values
 * from the last event leak into this one.
 */
export function gtmEcommercePush(event, ecommerce, params = {}) {
  if (!gtmEnabled()) return;
  safely('gtm', event, () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ...params, ecommerce });
    debug('gtm', event, { ...params, ecommerce });
  });
}

export function ga4Event(name, params = {}) {
  if (!ga4Enabled()) return;
  safely('ga4', name, () => {
    window.gtag('event', name, params);
    debug('ga4', name, params);
  });
}

export function pixelTrack(name, params = {}, { custom = false } = {}) {
  if (!pixelEnabled()) return;
  safely('pixel', name, () => {
    window.fbq(custom ? 'trackCustom' : 'track', name, params);
    debug('pixel', name, params);
  });
}

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const getCurrency = () =>
  typeof window !== 'undefined' ? window.storeConfig?.currencyCode : undefined;

/**
 * `payablePrice` on an order item is the *line* total — getItemPrice() already
 * multiplies through the selected size quantities — while GA4 and the Pixel
 * both want a per-unit price, so divide the quantity back out.
 */
export function toAnalyticsItems(menuItems = []) {
  return (menuItems || []).filter(Boolean).map((item, index) => {
    const quantity = Number(item.quantity) || 1;
    const lineTotal = Number(item.payablePrice ?? item.itemPrice) || 0;
    return {
      item_id: item._id,
      item_name: item.name,
      ...(item.categoryId && { item_category: item.categoryId }),
      price: round2(lineTotal / quantity),
      quantity,
      index,
    };
  });
}

const sumItemValue = (items) =>
  round2(items.reduce((total, item) => total + item.price * item.quantity, 0));

/**
 * What was just added to the cart, derived from the quantity delta between the
 * cart before and after the change.
 *
 * The item handed to engineAddToOrder() isn't priced yet, and the engine merges
 * a re-added item into its existing line rather than appending, so neither the
 * incoming item nor the last element of the new cart is reliably "the thing
 * that was added". `payablePrice` is scaled down with the quantity so the
 * per-unit price stays correct.
 */
export function diffAddedItems(before, after) {
  const keyOf = (item) => `${item._id}:${item.orderIndex ?? 0}`;
  const previous = new Map(
    (before || [])
      .filter(Boolean)
      .map((item) => [keyOf(item), Number(item.quantity) || 0])
  );

  return (after || []).filter(Boolean).reduce((added, item) => {
    const quantity = Number(item.quantity) || 0;
    const delta = quantity - (previous.get(keyOf(item)) || 0);
    if (delta <= 0) return added;

    const lineTotal = Number(item.payablePrice ?? item.itemPrice) || 0;
    added.push({
      ...item,
      quantity: delta,
      payablePrice: quantity > 0 ? (lineTotal / quantity) * delta : lineTotal,
    });
    return added;
  }, []);
}

function ga4Payload(items, value, extra = {}) {
  const currency = getCurrency();
  return {
    ...(value !== undefined && { value: round2(value) }),
    ...(currency && { currency }),
    items,
    ...extra,
  };
}

// Meta's `contents` mirrors GA4's `items` under different field names.
function pixelPayload(items, value, extra = {}) {
  const currency = getCurrency();
  return {
    content_type: 'product',
    content_ids: items.map((item) => item.item_id),
    contents: items.map((item) => ({
      id: item.item_id,
      quantity: item.quantity,
      item_price: item.price,
    })),
    num_items: items.reduce((total, item) => total + item.quantity, 0),
    ...(value !== undefined && { value: round2(value) }),
    ...(currency && { currency }),
    ...extra,
  };
}

/**
 * Dimensions worth carrying on every ecommerce event. `orderType` is a
 * MENU_ORDER_TYPE code, which is meaningless in a report, so send the label.
 */
export function orderContext(currentOrder) {
  const { orderType, paymentType } = currentOrder || {};
  const orderTypeLabel =
    orderType === undefined || orderType === null || orderType === ''
      ? undefined
      : MENU_ORDER_TYPE_MAPPINGS[orderType] ?? String(orderType);

  return {
    ...(orderTypeLabel && { order_type: orderTypeLabel }),
    ...(PAYMENT_TYPE_LABELS[paymentType] && {
      payment_type: PAYMENT_TYPE_LABELS[paymentType],
    }),
  };
}

/**
 * Fans one ecommerce event out to all three channels. GA4 takes the payload
 * flat, GTM takes the same payload nested under `ecommerce`, and the Pixel
 * takes its own field names.
 */
function emitEcommerce(ga4Name, pixelName, ga4Params, pixelParams) {
  const { value, currency, items, transaction_id, shipping, ...context } =
    ga4Params;
  gtmEcommercePush(
    ga4Name,
    {
      ...(transaction_id && { transaction_id }),
      ...(value !== undefined && { value }),
      ...(currency && { currency }),
      ...(shipping !== undefined && { shipping }),
      items,
    },
    context
  );
  ga4Event(ga4Name, ga4Params);
  if (pixelName) pixelTrack(pixelName, pixelParams);
}

/**
 * A menu item being opened. Menu items aren't order items yet, so they carry a
 * `unitPrice` rather than the `payablePrice` an in-cart item has.
 */
export function trackViewItem(item, unitPrice) {
  if (!item) return;
  const price = round2(unitPrice);
  const items = [
    {
      item_id: item._id,
      item_name: item.name,
      ...(item.categoryId && { item_category: item.categoryId }),
      ...(price > 0 && { price }),
      quantity: 1,
      index: 0,
    },
  ];
  const value = price > 0 ? price : undefined;
  emitEcommerce(
    'view_item',
    'ViewContent',
    ga4Payload(items, value),
    pixelPayload(items, value)
  );
}

/** One or more order items just added to the cart. */
export function trackAddToCart(addedItems, currentOrder) {
  const items = toAnalyticsItems(
    Array.isArray(addedItems) ? addedItems : [addedItems]
  );
  if (!items.length) return;

  const value = sumItemValue(items);
  const context = orderContext(currentOrder);
  emitEcommerce(
    'add_to_cart',
    'AddToCart',
    ga4Payload(items, value, context),
    pixelPayload(items, value, context)
  );
}

/** The customer confirmed their cart and moved on to payment. */
export function trackBeginCheckout(currentOrder) {
  const items = toAnalyticsItems(currentOrder?.menuItems);
  if (!items.length) return;

  const value = currentOrder?.payableAmount ?? sumItemValue(items);
  const context = orderContext(currentOrder);
  emitEcommerce(
    'begin_checkout',
    'InitiateCheckout',
    ga4Payload(items, value, context),
    pixelPayload(items, value, context)
  );
}

/** A payment method was chosen and the customer moved on. */
export function trackAddPaymentInfo(currentOrder) {
  const items = toAnalyticsItems(currentOrder?.menuItems);
  if (!items.length) return;

  const value = currentOrder?.payableAmount ?? sumItemValue(items);
  const context = orderContext(currentOrder);
  emitEcommerce(
    'add_payment_info',
    'AddPaymentInfo',
    ga4Payload(items, value, context),
    pixelPayload(items, value, context)
  );
}

function isDuplicatePurchase(transactionId) {
  if (!transactionId) return false;
  try {
    const seen = localStorage.get(TRACKED_PURCHASES_KEY) || [];
    if (seen.includes(transactionId)) return true;
    localStorage.set(
      TRACKED_PURCHASES_KEY,
      [...seen, transactionId].slice(-TRACKED_PURCHASES_KEPT)
    );
    return false;
  } catch (error) {
    debug('error', 'purchase dedupe', error);
    return false;
  }
}

/**
 * A completed, paid order. Safe to call from every payment path — the first
 * call for a given `transactionId` wins and the rest are dropped.
 */
export function trackPurchase(currentOrder, transactionId) {
  const items = toAnalyticsItems(currentOrder?.menuItems);
  if (!items.length) return;
  if (isDuplicatePurchase(transactionId)) {
    debug('skip', 'purchase (already tracked)', { transactionId });
    return;
  }

  const value = currentOrder?.payableAmount ?? sumItemValue(items);
  const shipping = Number(currentOrder?.deliveryCost) || 0;
  const extra = {
    ...orderContext(currentOrder),
    ...(transactionId && { transaction_id: String(transactionId) }),
    ...(shipping > 0 && { shipping: round2(shipping) }),
  };

  emitEcommerce(
    'purchase',
    'Purchase',
    ga4Payload(items, value, extra),
    pixelPayload(items, value, extra)
  );
}

// ---------------------------------------------------------------------------
// Screen tracking (see helpers/hooks/useScreenViewTracking.js)
// ---------------------------------------------------------------------------

// This is a single-page sidebar flow that never changes the URL, so GA4's
// automatic page_view fires once per session and its built-in "Pages and
// screens" report would show one row for an entire visit. Sending a synthetic
// page_view alongside the custom event makes the standard reports work with no
// per-store admin setup; `virtual_screen_view` stays for anyone who has already
// built triggers or custom reports on it.
function screenPageLocation(screenName) {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}/~/${screenName}`;
}

export function trackScreenView(screenName) {
  if (typeof window === 'undefined') return;

  // `engagement_time_msec: 1` marks the session as engaged from the very first
  // screen; real per-screen dwell time is reported by trackScreenEngagement.
  const params = { screen_name: screenName, engagement_time_msec: 1 };

  gtmPush('virtual_screen_view', params);
  ga4Event('virtual_screen_view', params);
  ga4Event('page_view', {
    page_location: screenPageLocation(screenName),
    page_title: screenName,
    screen_name: screenName,
  });
  pixelTrack(
    'VirtualScreenView',
    { screen_name: screenName },
    { custom: true }
  );
}

export function trackScreenEngagement(screenName, engagementTimeMsec) {
  const params = {
    screen_name: screenName,
    engagement_time_msec: engagementTimeMsec,
  };
  gtmPush('screen_engagement', params);
  ga4Event('screen_engagement', params);
}
